import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/Loaders";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle, XCircle, Clock, Hand, RocketLaunch, Database, ShieldCheck, HardDrives, Warning, Trash, CircleNotch, Broadcast, Wrench, UsersThree, Globe } from "@phosphor-icons/react";

const RS = {
  PASS: { badge: "bg-green-100 text-green-700", dot: "bg-green-500", ring: "border-green-200" },
  WARNING: { badge: "bg-amber-100 text-amber-700", dot: "bg-amber-500", ring: "border-amber-200" },
  BLOCKED: { badge: "bg-red-100 text-red-700", dot: "bg-red-500", ring: "border-red-300" },
};
const MODES = [
  { v: "MAINTENANCE", label: "Maintenance", icon: Wrench, desc: "Public site shows a maintenance page. Admins keep full access." },
  { v: "PRIVATE_BETA", label: "Private Beta", icon: UsersThree, desc: "Site is live for controlled testing with selected users." },
  { v: "PUBLIC_LAUNCH", label: "Public Launch", icon: Globe, desc: "Fully public. Requires zero launch blockers." },
];

const StatusIcon = ({ ok }) => ok
  ? <CheckCircle size={18} weight="fill" className="text-green-600 shrink-0" />
  : <XCircle size={18} weight="fill" className="text-stone-300 shrink-0" />;
const ChecklistIcon = ({ status }) => {
  if (status === "done") return <CheckCircle size={18} weight="fill" className="text-green-600 shrink-0" />;
  if (status === "manual") return <Hand size={18} weight="fill" className="text-blue-500 shrink-0" />;
  return <Clock size={18} weight="fill" className="text-amber-500 shrink-0" />;
};
const IntRow = ({ label, ok, extra }) => (
  <div className="flex items-center justify-between rounded-xl border border-stone-100 px-4 py-3">
    <span className="text-sm text-stone-700">{label}</span>
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${ok ? "bg-green-100 text-green-700" : "bg-stone-100 text-stone-500"}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${ok ? "bg-green-600" : "bg-stone-400"}`} />{ok ? "Configured" : "Not configured"}{extra ? ` · ${extra}` : ""}
    </span>
  </div>
);
const ReadinessCard = ({ r }) => {
  const s = RS[r.status] || RS.WARNING;
  return (
    <div data-testid={`readiness-${r.key}`} className={`rounded-2xl border bg-white p-4 ${s.ring}`}>
      <div className="flex items-center justify-between">
        <p className="font-heading font-semibold text-stone-900">{r.label}</p>
        <span data-testid={`readiness-${r.key}-status`} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${s.badge}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />{r.status}
        </span>
      </div>
      <p className="mt-1 text-xs text-stone-500">{r.note}</p>
    </div>
  );
};

export default function AdminLaunch() {
  const { user } = useAuth();
  const isSuper = (user?.admin_role || "SUPER") === "SUPER";
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [purging, setPurging] = useState(false);
  const [publicOpen, setPublicOpen] = useState(false);
  const [savingMode, setSavingMode] = useState(false);

  const load = () => api.get("/admin/launch-status").then((r) => setD(r.data)).catch(() => setErr(true));
  useEffect(() => { load(); }, []);

  const setMode = async (mode) => {
    setSavingMode(true);
    try {
      const r = await api.post("/admin/launch-mode", { mode });
      toast.success(`Launch status set to ${r.data.mode.replace("_", " ")}`);
      setPublicOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change launch status."); }
    finally { setSavingMode(false); }
  };
  const chooseMode = (v) => { if (v === "PUBLIC_LAUNCH") setPublicOpen(true); else setMode(v); };
  const toggleCheck = async (key, checked) => {
    try { await api.post("/admin/golive-checklist", { key, checked }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update checklist."); }
  };
  const purge = async () => {
    setPurging(true);
    try {
      const r = await api.post("/admin/purge-demo-data", { confirm });
      toast.success(`Demo data cleared — ${r.data.total} records removed.`);
      setPurgeOpen(false); setConfirm(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not purge data."); }
    finally { setPurging(false); }
  };

  if (err) return <DashboardLayout nav={adminNav} title="Admin"><div className="mt-10 rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700" data-testid="launch-error">Couldn't load launch status. Please refresh or try again shortly.</div></DashboardLayout>;
  if (!d) return <DashboardLayout nav={adminNav} title="Admin"><div className="flex justify-center py-24"><Spinner /></div></DashboardLayout>;

  const totals = d.readiness_totals || { passed: 0, warnings: 0, blockers: 0 };
  const blockers = d.blockers || [];
  const mode = d.launch_mode || "PRIVATE_BETA";

  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Launch Center</h1>
      <p className="mt-1 text-sm text-stone-500">Production readiness at a glance. Nothing is marked live unless it is actually configured.</p>

      {/* Launch Control */}
      <h2 className="mt-6 flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><Broadcast size={20} className="text-primary" /> Launch Control</h2>
      <p className="mt-1 text-sm text-stone-500">Current status: <span data-testid="current-launch-mode" className="font-semibold text-stone-800">{mode.replace("_", " ")}</span>. Public launch is a manual action and is blocked while critical blockers remain.</p>
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        {MODES.map((m) => {
          const active = mode === m.v;
          const disabled = !isSuper || savingMode || (m.v === "PUBLIC_LAUNCH" && blockers.length > 0);
          const Icon = m.icon;
          return (
            <button key={m.v} data-testid={`mode-${m.v}`} disabled={disabled && !active} onClick={() => !active && chooseMode(m.v)}
              className={`rounded-2xl border p-4 text-left transition-all ${active ? "border-primary bg-primary/5 ring-2 ring-primary/30" : "border-stone-200 bg-white hover:border-stone-300"} ${disabled && !active ? "cursor-not-allowed opacity-50" : ""}`}>
              <div className="flex items-center gap-2"><Icon size={20} weight="duotone" className={active ? "text-primary" : "text-stone-500"} /><span className="font-heading font-semibold text-stone-900">{m.label}</span>{active && <span className="ml-auto rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-white">ACTIVE</span>}</div>
              <p className="mt-1.5 text-xs text-stone-500">{m.desc}</p>
              {m.v === "PUBLIC_LAUNCH" && blockers.length > 0 && <p className="mt-1.5 text-[11px] font-semibold text-red-600">Blocked by {blockers.length} issue(s)</p>}
            </button>
          );
        })}
      </div>
      {!isSuper && <p className="mt-2 text-xs text-stone-400">Only a Super Admin can change launch status.</p>}

      {/* Launch blockers */}
      {blockers.length > 0 && (
        <div data-testid="launch-blockers" className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-5">
          <p className="flex items-center gap-2 font-heading font-semibold text-red-800"><Warning size={18} weight="fill" className="text-red-500" /> {blockers.length} launch blocker(s)</p>
          <ul className="mt-2 list-inside list-disc text-sm text-red-700">{blockers.map((b) => <li key={b}>{b}</li>)}</ul>
          <p className="mt-2 text-xs text-red-600">Resolve these before switching to Public Launch.</p>
        </div>
      )}

      {/* Production Readiness Score */}
      <h2 className="mt-8 flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><RocketLaunch size={20} className="text-primary" /> Production Readiness Score</h2>
      <div className="mt-2 flex flex-wrap gap-2 text-sm" data-testid="readiness-totals">
        <span className="rounded-full bg-green-100 px-3 py-1 font-semibold text-green-700">{totals.passed} Passed</span>
        <span className="rounded-full bg-amber-100 px-3 py-1 font-semibold text-amber-700">{totals.warnings} Warnings</span>
        <span className="rounded-full bg-red-100 px-3 py-1 font-semibold text-red-700">{totals.blockers} Blockers</span>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {(d.readiness || []).map((r) => <ReadinessCard key={r.key} r={r} />)}
      </div>

      {/* Go-Live checklist */}
      <h2 className="mt-8 flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><CheckCircle size={20} className="text-primary" /> Go-Live Checklist</h2>
      <p className="mt-1 text-sm text-stone-500">Auto items reflect live configuration. Manual items are yours to confirm and tick off.</p>
      <div className="mt-3 grid gap-2 md:grid-cols-2">
        {(d.golive_checklist || []).map((c) => (
          <label key={c.key} data-testid={`golive-${c.key}`} className={`flex items-center gap-3 rounded-xl border border-stone-100 bg-white px-4 py-2.5 ${c.type === "manual" && isSuper ? "cursor-pointer hover:bg-accent" : ""}`}>
            {c.type === "manual" ? (
              <input type="checkbox" data-testid={`golive-${c.key}-toggle`} disabled={!isSuper} checked={c.done} onChange={(e) => toggleCheck(c.key, e.target.checked)} className="h-4 w-4 accent-green-600" />
            ) : (
              <StatusIcon ok={c.done} />
            )}
            <span className="flex-1 text-sm text-stone-700">{c.label}</span>
            <span className={`text-xs font-medium ${c.done ? "text-green-600" : c.type === "manual" ? "text-blue-500" : "text-amber-500"}`}>{c.done ? "done" : c.type === "manual" ? "manual" : "pending"}</span>
          </label>
        ))}
      </div>

      {/* Critical checks */}
      <h2 className="mt-8 flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><ShieldCheck size={20} className="text-primary" /> Critical checks</h2>
      <div className="mt-3 grid gap-2 md:grid-cols-2">
        {d.critical.map((c) => (
          <div key={c.key} data-testid={`critical-${c.key}`} className="flex items-center gap-3 rounded-xl border border-stone-100 bg-white px-4 py-3">
            <StatusIcon ok={c.ok} /><span className="text-sm text-stone-700">{c.label}</span>
          </div>
        ))}
      </div>

      {/* Integrations */}
      <h2 className="mt-8 font-heading text-lg font-semibold text-stone-900">Integrations</h2>
      <div className="mt-3 grid gap-2 md:grid-cols-2">
        <IntRow label="Razorpay Payments" ok={d.integrations.razorpay.configured} extra={d.integrations.razorpay.mode.toUpperCase()} />
        <IntRow label="Razorpay Webhook" ok={d.integrations.razorpay.webhook_configured} />
        <IntRow label="Google Maps" ok={d.integrations.maps.configured} />
        <IntRow label="WhatsApp (Twilio)" ok={d.integrations.whatsapp.configured} extra={d.integrations.whatsapp.configured ? null : "click-to-WhatsApp"} />
        <IntRow label="Email" ok={d.integrations.email.configured} />
        <IntRow label="Analytics" ok={d.integrations.analytics.configured} />
      </div>
      <p className="mt-2 text-xs text-stone-400">Secrets are never displayed — only whether each service is configured. Domain: {d.domain.site_url || "not set"} {d.domain.https ? "(HTTPS)" : "(no HTTPS)"}.</p>

      {/* Database health */}
      <h2 className="mt-8 flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><Database size={20} className="text-primary" /> Database health</h2>
      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        {[["Reachable", d.database.ok ? "Yes" : "No"], ["Bookings", d.database.bookings], ["Payments", d.database.payments], ["Duplicate IDs", d.database.duplicate_booking_refs], ["Orphaned payments", d.database.orphaned_payments]].map(([l, v]) => (
          <div key={l} className="rounded-xl border border-stone-100 bg-white p-4 text-center"><p className="font-heading text-xl font-bold text-stone-900">{v}</p><p className="text-xs text-stone-400">{l}</p></div>
        ))}
      </div>

      {/* Backup */}
      <h2 className="mt-8 flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><HardDrives size={20} className="text-primary" /> Backup & recovery</h2>
      <div className="mt-3 rounded-2xl border border-stone-200 bg-white p-5 text-sm text-stone-600">
        <p><strong>Strategy:</strong> {d.backup.strategy}</p>
        <p className="mt-1"><strong>Frequency:</strong> {d.backup.frequency}</p>
        <p className="mt-1"><strong>Restore:</strong> {d.backup.restore}</p>
        <p className="mt-2 text-xs text-stone-400">{d.backup.note}</p>
      </div>

      {/* Go-live: clear demo data */}
      <h2 className="mt-10 flex items-center gap-2 font-heading text-lg font-semibold text-red-700"><Warning size={20} weight="fill" className="text-red-500" /> Go-Live: Clear Demo Data</h2>
      <div className="mt-3 rounded-2xl border-2 border-dashed border-red-200 bg-red-50/50 p-5">
        <p className="text-sm text-stone-600">Start production with a clean slate. This permanently deletes all <strong>bookings, payments, refunds, disputes, support tickets, leads, reviews, payouts and notifications</strong>. Your <strong>properties, user accounts, catalog and settings are kept</strong>.</p>
        {isSuper ? (
          <Button data-testid="purge-open-btn" onClick={() => setPurgeOpen(true)} className="mt-4 gap-2 rounded-xl bg-red-600 hover:bg-red-700"><Trash size={16} /> Clear Demo Data</Button>
        ) : (
          <p className="mt-3 text-xs font-medium text-stone-500">Only a Super Admin can perform this action.</p>
        )}
      </div>

      {/* Public launch confirm */}
      <Dialog open={publicOpen} onOpenChange={setPublicOpen}>
        <DialogContent data-testid="public-launch-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-heading"><Globe size={20} className="text-primary" /> Go Public?</DialogTitle></DialogHeader>
          <p className="text-sm text-stone-600">Are you sure you want to make Avinya Bookings <strong>publicly available</strong> to everyone? Make sure your real properties are verified and payments are live.</p>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setPublicOpen(false)}>Cancel</Button>
            <Button data-testid="public-launch-confirm" disabled={savingMode} className="rounded-xl bg-primary hover:bg-green-900" onClick={() => setMode("PUBLIC_LAUNCH")}>{savingMode ? "Launching…" : "Yes, Launch Publicly"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purge confirm */}
      <Dialog open={purgeOpen} onOpenChange={(v) => { if (!v) { setPurgeOpen(false); setConfirm(""); } }}>
        <DialogContent data-testid="purge-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-700"><Warning size={20} weight="fill" className="text-red-500" /> Clear all demo data?</DialogTitle></DialogHeader>
          <div className="text-sm text-stone-600">
            <p>This cannot be undone. All transactional and demo records will be permanently removed so you can launch clean.</p>
            <p className="mt-3 mb-1.5 font-medium text-stone-700">Type <span className="rounded bg-stone-100 px-1.5 py-0.5 font-mono font-bold text-red-600">DELETE</span> to confirm:</p>
            <Input data-testid="purge-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE" className="rounded-xl" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => { setPurgeOpen(false); setConfirm(""); }}>Cancel</Button>
            <Button data-testid="purge-confirm-btn" disabled={confirm !== "DELETE" || purging} className="gap-2 rounded-xl bg-red-600 hover:bg-red-700" onClick={purge}>
              {purging ? <><CircleNotch size={16} className="animate-spin" /> Clearing…</> : <><Trash size={16} /> Permanently Delete</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
